/* Scroll reveal — the theme's one entrance-animation engine.
 *
 * Contract (see docs/motion-system-plan.md):
 *
 *   <div data-reveal>                  rises and fades in when it scrolls into view
 *   <div data-reveal='fade'>           opacity only, no travel
 *   <div data-reveal-group>            its element children reveal in sequence
 *   <div data-reveal data-reveal-delay='3'>   join a sequence without being in the group
 *   <div data-reveal-skip>             this element and its subtree are left alone
 *
 * Everything visual lives in the `Motion` block at the end of assets/app.scss. This file only
 * decides *when*: it adds `.is-revealed` and gets out of the way. Keeping the two apart is what
 * lets the CSS carry the reduced-motion and no-JS fallbacks on its own.
 *
 * Nothing here runs unless `html.motion-reveal` is present. That class is set by an inline
 * script in layout/theme.liquid, which refuses when the visitor prefers reduced motion, when
 * IntersectionObserver is missing, or when the merchant has switched the setting off.
 */

/* Everything below is wrapped in an IIFE, and that is not stylistic.
 *
 * The assets in this theme are classic scripts, not modules, and a top-level `const` in a
 * classic script does not scope to its file — it goes into the shared global lexical scope,
 * where every other asset can collide with it. The first version of this file declared
 * `let observer` at the top level and assets/theme.js:1518 already had `const observer` for its
 * MutationObserver; the result was `SyntaxError: Identifier 'observer' has already been
 * declared` and this whole file refusing to execute. (The page still rendered correctly, which
 * is the fail-safe in layout/theme.liquid earning its keep — but nothing animated.)
 *
 * Do not unwrap this. Nothing outside needs to read anything in here.
 */
(function () {
  const REVEALED_CLASS = 'is-revealed';

  /* Sections the engine must never touch, matched against the `.shopify-section` wrapper.
   *
   * Not a style preference — each one would break something:
   *
   *   header / minimal-header  the sticky header and its overlay are `position: fixed`, and an
   *                            ancestor with a transform (or an opacity below 1) becomes their
   *                            containing block, which pins them to the header instead of the
   *                            viewport.
   *   footer                   chrome, not content. A full-width navy block fading in at the
   *                            bottom of every page is noise, not polish.
   *   hero                     holds the LCP image. Never hide it — this is the single most
   *                            expensive animation mistake a theme can make.
   *   main-product             above the fold, conversion-critical, and its own sibling sticky
   *                            add-to-cart bar is `position: fixed`.
   *   main-cart                conversion-critical, and re-renders itself constantly.
   *   explore-experiences      holds its subtree out of the render tree with
   *                            `content-visibility: hidden` and reveals it with its own observer,
   *                            as the fix for an iOS pinch-zoom jetsam crash. A second observer
   *                            and a second set of layers is exactly what that fix exists to avoid.
   *   about-story              contains a `position: sticky` column, whose travel is measured
   *                            against the nearest transformed ancestor.
   */
  const SKIPPED_SECTIONS = [
    '#shopify-section-header',
    '#shopify-section-minimal-header',
    '#shopify-section-footer',
    '.section-hero',
    '.section-main-product',
    '.section-main-cart',
    '.section-explore-experiences',
    '.section-about-story',
  ].join(',');

  /* How far into the viewport an element must travel before it reveals. Negative bottom margin
   * shrinks the observed box upward, so nothing fires while it is still clipping the bottom edge —
   * without it, elements animate in the corner of the eye and the page looks like it is twitching. */
  const ROOT_MARGIN = '0px 0px -12% 0px';

  /* A stagger is a rhythm, not a queue. Past half a dozen steps the tail is just latency, so a
   * twenty-card grid reveals its first six in sequence and the rest together. */
  const MAX_STAGGER_STEPS = 6;

  let observer = null;

  function isEnabled() {
    return document.documentElement.classList.contains('motion-reveal');
  }

  /* Hiding must never animate, and getting that wrong is invisible in the markup.
   *
   * `data-reveal` is written from here, at defer time, onto elements the browser has already
   * styled at full opacity. So the move to `opacity: 0` is a property change made while the
   * matching `transition` is live, and the element fades *out* over --shop-motion-slow before it
   * has any chance to fade in. Content is painted, then wiped, then brought back — which is not
   * an entrance, it is a flicker, and it is why the reveal reads as laggy on the way down a page.
   *
   * The whole fix is `transition-property: none` for the priming frame: the hidden state lands in
   * one go, and `releasePrimed` hands the transition back once the browser has computed it.
   * Restoring a transition property does not itself change opacity, so nothing starts on release.
   *
   * Every write that changes a reveal state goes through `prime` for the same reason — including
   * `revealImmediately`, whose contract is precisely that it must not animate. */
  const primed = [];

  function prime(element) {
    element.style.transitionProperty = 'none';
    primed.push(element);
  }

  function releasePrimed() {
    if (primed.length === 0) return;

    /* Force the primed state to be computed while the transition is still off. `observe` already
     * reads layout for the above-fold guard, but only for the elements a given pass returned; an
     * explicit flush keeps this correct however that changes. */
    document.body.getBoundingClientRect();

    primed.forEach((element) => {
      element.style.transitionProperty = '';
    });

    primed.length = 0;
  }

  function tag(element, variant) {
    prime(element);
    element.setAttribute('data-reveal', variant);
  }

  /* Reveal without animating: set the end state in the same synchronous block that sets
   * `data-reveal`, so the browser never gets a frame in which the element is hidden. This is how
   * anything already on screen is handled — it is marked done, not marked done quickly. */
  function revealImmediately(element) {
    prime(element);
    element.classList.add(REVEALED_CLASS);
  }

  /* True when the element is inside, or overlapping, the first screenful.
   *
   * The above-the-fold guard, and the reason this engine cannot regress LCP: an element the
   * visitor can already see is never assigned `opacity: 0` in the first place. It also covers the
   * restored-scroll case on a back navigation, where "the fold" is wherever the browser put us.
   *
   * Measured at defer time, so images may not have loaded and rects can still shift. That is fine:
   * the question is only "is this roughly on screen", and the comparison is deliberately generous. */
  function isWithinFirstViewport(element) {
    const rect = element.getBoundingClientRect();
    return rect.top < window.innerHeight;
  }

  function observe(element) {
    if (isWithinFirstViewport(element)) {
      revealImmediately(element);
      return;
    }

    observer.observe(element);
  }

  /* Turn a [data-reveal-group] into a sequence: every element child becomes a reveal target and
   * carries its own index. The index is a bare number; app.scss multiplies it by the stagger token,
   * so the beat stays a design decision and this stays an ordering decision. */
  function expandGroup(group) {
    if (group.dataset.revealGroupReady === 'true') return;
    group.dataset.revealGroupReady = 'true';

    const variant = group.getAttribute('data-reveal-group');
    const children = [...group.children];

    children.forEach((child, index) => {
      if (
        child.hasAttribute('data-reveal') ||
        child.hasAttribute('data-reveal-skip')
      )
        return;

      tag(child, variant || '');
      child.style.setProperty(
        '--reveal-delay',
        String(Math.min(index, MAX_STAGGER_STEPS)),
      );
    });
  }

  /* The zero-effort tier: give every section a gentle entrance without editing 46 section files.
   *
   * Tags the section's first element child rather than the `.shopify-section` wrapper itself. The
   * wrapper is Shopify's, the theme editor writes to it, and sections that render more than one
   * top-level node (the header and its overlay, for one) keep their later siblings untouched this
   * way — which is precisely what makes a `position: fixed` sibling safe.
   *
   * A section that already says something about its own motion is left alone entirely: explicit
   * markup beats the blanket rule, so adding `data-reveal-group` to a card grid replaces the
   * section-wide fade instead of layering a second animation on top of it.
   *
   * Tagged `fade` rather than the default rise, and that is a correctness constraint rather than
   * a taste call. What gets tagged here is the section's own full-bleed box, and its top edge is
   * a *seam*: sections in this theme butt straight against each other, and the ones with rounded
   * top corners tuck under their neighbour with a negative margin (`-mt-6` on the footer, `-mt-8`
   * on shop-by-category, `-my-8` on featured-in) precisely so those arcs pick up the colour above
   * them instead of the page canvas.
   *
   * A transform does not move the section above with it. So translating this box down by
   * `--shop-motion-rise` prises that seam open and paints ~16px of bare canvas -- browser white,
   * since neither `html` nor `body` carries a background -- across the full width for the length
   * of the animation, with the rounded corners cutting notches out of it. It reads as a white
   * band flashing in and out at the top of every section on the way down the page.
   *
   * Content *inside* a section owns no seam and still rises; it is only the scenery that is held
   * still. Which is also why this costs nothing visually: 16px of travel on a full-bleed block is
   * imperceptible next to the same 16px on a heading or a card. */
  function autoTagSections(root) {
    root.querySelectorAll('.shopify-section').forEach((section) => {
      if (section.matches(SKIPPED_SECTIONS)) return;
      if (section.querySelector('[data-reveal], [data-reveal-group]')) return;

      const box = section.firstElementChild;

      if (!box) return;
      if (box.hasAttribute('data-reveal-skip')) return;

      const scenery = sceneryBox(box);

      /* Nothing in this section paints, so there is no seam to prise open: the blanket fade
       * applies to the section's own box exactly as it always has. */
      if (!paintsBackground(scenery)) {
        tag(box, 'fade');
        return;
      }

      /* Scenery: tag what is inside it instead. Content containers only — the absolutely
       * positioned bits a section pins to its own edges (scroll anchors, the corner fills in
       * snippets/section-rounded-corner-fills.liquid) are part of the box, not of what it holds. */
      scenery.querySelectorAll(':scope > .container').forEach((content) => {
        if (content.hasAttribute('data-reveal-skip')) return;

        tag(content, 'fade');
      });
    });
  }

  /* `rgba(...)` with a zero alpha is what both `transparent` and an unset background compute to.
   * Anything else puts paint on the page. */
  const TRANSPARENT = /^(transparent|rgba\(.*,\s*0\s*\))$/;

  /* True when this box paints its own background, and therefore may not be faded.
   *
   * The companion to the seam note above, and the more expensive half of it. Nothing sits behind
   * a full-bleed section but the page canvas, and neither `html` nor `body` carries a background
   * here — so an opacity below 1 on that box does not soften the section, it paints browser white
   * across the full width of the page.
   *
   * That is worse than the transform hazard, because it lasts the entire animation *and* starts
   * before it. ROOT_MARGIN holds the reveal back until the section's top edge is 12% of the
   * viewport past the bottom, so roughly 100px of the section is already on screen as bare canvas
   * before the fade so much as begins. On the reviews section it read as a white band above
   * "Real gifts. Real reactions. Real moments." that turned blue about a second later.
   *
   * So a painted box keeps its paint and its content animates instead — the same split the note
   * above already draws between scenery and content. A painted box with no content container to
   * descend into is left alone entirely: no animation beats a flash of the wrong colour. */
  function paintsBackground(element) {
    const styles = getComputedStyle(element);

    return (
      styles.backgroundImage !== 'none' ||
      !TRANSPARENT.test(styles.backgroundColor)
    );
  }

  /* The box that actually paints a section, which is not always the section's first child.
   *
   * sections/choice-banner.liquid wraps its full-bleed band in a bare `relative` div, because
   * `section-rounded-corner-fills` needs something to position against — so the outer box is
   * transparent and the band a layer down. Hiding the wrapper hides the band all the same.
   *
   * Descend through wrappers that paint nothing and hold a single in-flow child: a box shaped
   * like that is a positioning device, not content. Out-of-flow children — the corner fills, the
   * scroll anchor in customers-reviews — are pinned to the wrapper's own edges and belong to the
   * scenery, so they do not count towards that child. `.container` is where content starts and is
   * therefore the floor; below it there is nothing full-bleed left to find. */
  function sceneryBox(element) {
    let box = element;

    while (!paintsBackground(box) && !box.classList.contains('container')) {
      const inFlow = [...box.children].filter((child) => {
        const position = getComputedStyle(child).position;

        return position !== 'absolute' && position !== 'fixed';
      });

      if (inFlow.length !== 1) break;

      box = inFlow[0];
    }

    return box;
  }

  /* An explicit place in the running order for an element that is not inside a group -- the
   * call-to-action under a staggered card grid, say, which should land after the last card
   * rather than alongside the heading. Same unit as the index `expandGroup` writes: a bare
   * number that app.scss multiplies by the stagger token, so markup never spells out a
   * duration. */
  function applyExplicitDelay(element) {
    const delay = element.getAttribute('data-reveal-delay');

    if (delay) element.style.setProperty('--reveal-delay', delay);
  }

  function collectTargets(root) {
    autoTagSections(root);

    root.querySelectorAll('[data-reveal-group]').forEach((group) => {
      if (group.closest('[data-reveal-skip]')) return;
      expandGroup(group);
    });

    return [...root.querySelectorAll('[data-reveal]')].filter((element) => {
      if (element.classList.contains(REVEALED_CLASS)) return false;
      if (element.closest('[data-reveal-skip]')) return false;

      applyExplicitDelay(element);

      return true;
    });
  }

  function handleIntersections(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      entry.target.classList.add(REVEALED_CLASS);

      /* Reveal once. Elements are never re-hidden on the way back up: re-animating on every pass
       * is the fastest way to make a reveal system tiresome, and it would otherwise keep a live
       * observer entry on every node for the life of the page.
       *
       * Note what is deliberately absent — `will-change`. opacity and transform are already
       * composited without it, and this theme has a documented iOS jetsam crash caused by too
       * many promoted layers on one page. Hinting a hundred of them would be trading a real
       * crash for an imaginary frame. */
      observer.unobserve(entry.target);
    });
  }

  function scan(root) {
    const targets = collectTargets(root);

    targets.forEach(observe);
    releasePrimed();
  }

  function init() {
    if (!isEnabled()) return;

    observer = new IntersectionObserver(handleIntersections, {
      rootMargin: ROOT_MARGIN,
      threshold: 0,
    });

    scan(document);

    /* The theme editor swaps a section's markup in place without reloading, so anything it
     * re-renders arrives untagged. Re-scanning that subtree keeps the editor preview honest —
     * otherwise a merchant editing a section watches it stop animating. */
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target instanceof Element) scan(event.target);
    });
  }

  /* `defer` means this runs after the document is parsed but before DOMContentLoaded, which is
   * exactly when we want it: the DOM is complete, layout is measurable for the above-fold guard,
   * and nothing has been hidden for any longer than it had to be. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
